/**
 * Hardcoded fallback content for the 4 legal pages. The LegalPage renderer
 * ALWAYS attempts to fetch from `/api/content/legal/{slug}` first so admins
 * can edit copy via the CMS; this file is the safety net if the CMS entry
 * is missing or the API is unreachable.
 *
 * NOTE: `body_html` is intentionally plain semantic HTML (no scripts, no
 * inline styles). It is sanitized with DOMPurify before render.
 * Anchor ids on <h2> tags MUST match the `toc[].id` values.
 */

const LAUNCH_DATE = "February 1, 2026";
const CONTACT_EMAIL = "medicalpeptides@gmail.com";

const PRIVACY = {
  slug: "privacy-policy",
  title: "Privacy Policy",
  seo_title: "Privacy Policy | Medical Peptides Powered by VidaVital Medical",
  meta_description:
    "Learn how MedicalPeptides.ai collects, uses, stores, and protects your information when visiting our educational website.",
  last_updated_on: LAUNCH_DATE,
  toc: [
    { id: "information-we-collect", label: "Information We Collect" },
    { id: "how-we-use-information", label: "How We Use Information" },
    { id: "cookies", label: "Cookies" },
    { id: "third-party-services", label: "Third-Party Services" },
    { id: "hipaa", label: "HIPAA" },
    { id: "security", label: "Security" },
    { id: "your-rights", label: "Your Rights" },
    { id: "contact", label: "Contact" },
  ],
  body_html: `
    <p>Medical Peptides Powered by VidaVital Medical ("MedicalPeptides.ai," "we," "our," or "us") values your privacy and is committed to protecting your personal information.</p>

    <h2 id="information-we-collect">Information We Collect</h2>
    <p>We may collect:</p>
    <ul>
      <li>Name</li>
      <li>Email address</li>
      <li>Phone number</li>
      <li>Information submitted through consultation request forms</li>
      <li>IP address</li>
      <li>Browser type</li>
      <li>Device information</li>
      <li>Website usage analytics</li>
      <li>Cookies and similar technologies</li>
    </ul>

    <h2 id="how-we-use-information">How We Use Information</h2>
    <p>Information may be used to:</p>
    <ul>
      <li>Respond to inquiries</li>
      <li>Schedule consultations</li>
      <li>Improve our website</li>
      <li>Provide educational resources</li>
      <li>Monitor website performance</li>
      <li>Prevent fraud and abuse</li>
      <li>Comply with applicable laws</li>
    </ul>

    <h2 id="cookies">Cookies</h2>
    <p>Our website uses cookies to:</p>
    <ul>
      <li>Improve user experience</li>
      <li>Measure website traffic</li>
      <li>Remember preferences</li>
      <li>Analyze visitor behavior</li>
    </ul>
    <p>Users may disable cookies through their browser settings.</p>

    <h2 id="third-party-services">Third-Party Services</h2>
    <p>We may use services including:</p>
    <ul>
      <li>Google Analytics</li>
      <li>Google Tag Manager</li>
      <li>Microsoft Clarity</li>
      <li>Cloudflare</li>
      <li>Email marketing platforms</li>
    </ul>
    <p>These providers maintain their own privacy policies.</p>

    <h2 id="hipaa">HIPAA</h2>
    <p>MedicalPeptides.ai is primarily an educational website. Submitting information through the website does not establish a provider-patient relationship. Protected Health Information is handled according to applicable privacy laws when collected during the consultation process.</p>

    <h2 id="security">Security</h2>
    <p>We implement administrative, technical, and physical safeguards designed to protect your information. However, no internet transmission can be guaranteed to be completely secure.</p>

    <h2 id="your-rights">Your Rights</h2>
    <p>Depending on your jurisdiction, you may have rights to:</p>
    <ul>
      <li>Access your information</li>
      <li>Correct inaccurate information</li>
      <li>Request deletion</li>
      <li>Withdraw consent where applicable</li>
    </ul>

    <h2 id="contact">Contact</h2>
    <p>For privacy questions: <a href="mailto:${CONTACT_EMAIL}">${CONTACT_EMAIL}</a></p>
  `,
};

const TERMS = {
  slug: "terms-of-use",
  title: "Terms of Use",
  seo_title: "Terms of Use | Medical Peptides",
  meta_description:
    "Review the Terms of Use for MedicalPeptides.ai — an educational website powered by VidaVital Medical. Not medical advice.",
  last_updated_on: LAUNCH_DATE,
  toc: [
    { id: "acceptance", label: "Acceptance" },
    { id: "educational-purpose", label: "Educational Purpose" },
    { id: "no-medical-advice", label: "No Medical Advice" },
    { id: "eligibility", label: "Eligibility" },
    { id: "intellectual-property", label: "Intellectual Property" },
    { id: "user-conduct", label: "User Conduct" },
    { id: "third-party-links", label: "Third-Party Links" },
    { id: "limitation-of-liability", label: "Limitation of Liability" },
    { id: "governing-law", label: "Governing Law" },
  ],
  body_html: `
    <h2 id="acceptance">Acceptance</h2>
    <p>By using MedicalPeptides.ai, you agree to these Terms of Use. If you do not agree, discontinue use of the website.</p>

    <h2 id="educational-purpose">Educational Purpose</h2>
    <p>MedicalPeptides.ai is intended solely for educational and informational purposes. Content should not replace professional medical advice.</p>

    <h2 id="no-medical-advice">No Medical Advice</h2>
    <p>Nothing on this website constitutes:</p>
    <ul>
      <li>Medical advice</li>
      <li>Diagnosis</li>
      <li>Treatment recommendations</li>
      <li>Prescription guidance</li>
    </ul>
    <p>Always consult a licensed healthcare provider.</p>

    <h2 id="eligibility">Eligibility</h2>
    <p>Users must be at least 18 years old.</p>

    <h2 id="intellectual-property">Intellectual Property</h2>
    <p>All content including:</p>
    <ul>
      <li>Articles</li>
      <li>Graphics</li>
      <li>Logos</li>
      <li>Images</li>
      <li>Videos</li>
      <li>Databases</li>
      <li>Educational materials</li>
    </ul>
    <p>are owned by Medical Peptides Powered by VidaVital Medical unless otherwise stated. Unauthorized reproduction is prohibited.</p>

    <h2 id="user-conduct">User Conduct</h2>
    <p>Users agree not to:</p>
    <ul>
      <li>Hack the website</li>
      <li>Submit malicious content</li>
      <li>Attempt unauthorized access</li>
      <li>Interfere with website functionality</li>
    </ul>

    <h2 id="third-party-links">Third-Party Links</h2>
    <p>External websites are provided for convenience. MedicalPeptides.ai is not responsible for their content.</p>

    <h2 id="limitation-of-liability">Limitation of Liability</h2>
    <p>MedicalPeptides.ai shall not be liable for any damages arising from use of the website.</p>

    <h2 id="governing-law">Governing Law</h2>
    <p>These Terms shall be governed by applicable laws in the jurisdiction where VidaVital Medical operates.</p>
  `,
};

const TELEHEALTH = {
  slug: "telehealth-consent",
  title: "Telehealth Consent",
  seo_title: "Telehealth Consent Information | Medical Peptides",
  meta_description:
    "Understand the risks, benefits, alternatives, and privacy considerations associated with telehealth consultations through VidaVital Medical.",
  last_updated_on: LAUNCH_DATE,
  toc: [
    { id: "telehealth-services", label: "Telehealth Services" },
    { id: "risks", label: "Risks" },
    { id: "benefits", label: "Benefits" },
    { id: "alternatives", label: "Alternatives" },
    { id: "privacy", label: "Privacy" },
    { id: "emergency-situations", label: "Emergency Situations" },
    { id: "consent", label: "Consent" },
  ],
  body_html: `
    <h2 id="telehealth-services">Telehealth Services</h2>
    <p>Certain consultations may be conducted through secure telehealth technologies when appropriate and permitted by applicable law.</p>

    <h2 id="risks">Risks</h2>
    <p>Potential risks include:</p>
    <ul>
      <li>Technology failures</li>
      <li>Interrupted communications</li>
      <li>Internet connectivity issues</li>
    </ul>

    <h2 id="benefits">Benefits</h2>
    <p>Potential benefits include:</p>
    <ul>
      <li>Convenience</li>
      <li>Improved access</li>
      <li>Reduced travel</li>
      <li>Timely follow-up</li>
    </ul>

    <h2 id="alternatives">Alternatives</h2>
    <p>Patients may request in-person appointments whenever available and clinically appropriate.</p>

    <h2 id="privacy">Privacy</h2>
    <p>Telehealth visits are conducted using secure technologies intended to protect patient privacy.</p>

    <h2 id="emergency-situations">Emergency Situations</h2>
    <p>Telehealth should never be used during a medical emergency. Call 911 or seek immediate emergency medical care.</p>

    <h2 id="consent">Consent</h2>
    <p>By scheduling a telehealth consultation, you acknowledge your understanding of:</p>
    <ul>
      <li>Risks</li>
      <li>Benefits</li>
      <li>Alternatives</li>
    </ul>
  `,
};

const DISCLAIMER = {
  slug: "medical-disclaimer",
  title: "Medical Disclaimer",
  seo_title: "Medical Disclaimer | Medical Peptides",
  meta_description:
    "MedicalPeptides.ai is an educational resource. This disclaimer explains what our content is — and is not — intended to provide.",
  last_updated_on: LAUNCH_DATE,
  toc: [
    { id: "educational-resource", label: "Educational Resource" },
    { id: "no-medical-advice", label: "No Medical Advice" },
    { id: "individual-results", label: "Individual Results" },
    { id: "provider-consultation", label: "Provider Consultation" },
    { id: "fda-status", label: "FDA Status" },
    { id: "research", label: "Research" },
    { id: "emergency-care", label: "Emergency Care" },
    { id: "no-provider-patient-relationship", label: "No Provider-Patient Relationship" },
  ],
  body_html: `
    <h2 id="educational-resource">Educational Resource</h2>
    <p>MedicalPeptides.ai is an educational website designed to provide general information regarding peptide therapy and related wellness topics.</p>

    <h2 id="no-medical-advice">No Medical Advice</h2>
    <p>Content should never replace:</p>
    <ul>
      <li>Physician recommendations</li>
      <li>Diagnosis</li>
      <li>Medical evaluation</li>
      <li>Individual treatment plans</li>
    </ul>

    <h2 id="individual-results">Individual Results</h2>
    <p>Responses to therapies vary among individuals. No outcome is guaranteed.</p>

    <h2 id="provider-consultation">Provider Consultation</h2>
    <p>Treatment recommendations are made only after evaluation by a licensed medical provider.</p>

    <h2 id="fda-status">FDA Status</h2>
    <p>Some therapies discussed may involve medications prescribed for off-label use, compounded preparations, or products under active scientific investigation. Information provided does not imply FDA approval for every discussed use.</p>

    <h2 id="research">Research</h2>
    <p>Scientific understanding continues to evolve. Educational content reflects current knowledge at the time of publication.</p>

    <h2 id="emergency-care">Emergency Care</h2>
    <p>MedicalPeptides.ai is not intended for emergency medical situations. Call 911 immediately for emergencies.</p>

    <h2 id="no-provider-patient-relationship">No Provider-Patient Relationship</h2>
    <p>Viewing this website does not establish a provider-patient relationship. A provider-patient relationship begins only after a licensed provider accepts you as a patient following the appropriate evaluation.</p>
  `,
};

export const LEGAL_PAGES = {
  "privacy-policy": PRIVACY,
  "terms-of-use": TERMS,
  "telehealth-consent": TELEHEALTH,
  "medical-disclaimer": DISCLAIMER,
};

export const LEGAL_SLUGS = Object.keys(LEGAL_PAGES);
